import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { EntityTypeEnum } from "@app/projects/enumerators";
import ProviderAffiliationService from "@app/projects/providerAffiliationsService";
import { BenchmarkingProviderFlyoutData } from "@app/overviews/network-benchmarking-dashboard/dashboard-details/shared/BenchmarkingProviderFlyoutData";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared/NetworkComparisonFiltersPayload";
import { BenchmarkingServiceType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/utility";

interface BenchmarkingProviderFlyoutQueryData {
  entityType: EntityTypeEnum;
  provider: BenchmarkingProviderFlyoutData;
}

export interface BenchmarkingProviderFlyoutDataResult {
  entityType: EntityTypeEnum;
  isLoading: boolean;
  providerFlyoutData: BenchmarkingProviderFlyoutData | undefined;
}

export const BENCHMARKING_PROVIDER_FLYOUT_DATA_QUERY_KEY_PREFIX = "benchmarking-provider-flyout-data";

export const useBenchmarkingProviderFlyoutData = (
  service: BenchmarkingServiceType,
  payload: NetworkComparisonFiltersPayload | null
): BenchmarkingProviderFlyoutDataResult => {
  const providerAffiliationService = useMemo(() => new ProviderAffiliationService(), []);

  const queryResult = useQuery<BenchmarkingProviderFlyoutQueryData>({
    enabled: !!payload,
    queryFn: async ({ signal }) => {
      const [provider, entityType] = await Promise.all([
        service.getProviderFlyoutData(payload, signal),
        providerAffiliationService.GetNPPESEntityTypeByNPI(payload.npi, signal),
      ]);

      return { entityType: entityType || EntityTypeEnum.Unknown, provider };
    },
    queryKey: [BENCHMARKING_PROVIDER_FLYOUT_DATA_QUERY_KEY_PREFIX, service?.lineOfBusiness, JSON.stringify(payload)],
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  return {
    entityType: queryResult.data?.entityType ?? EntityTypeEnum.Unknown,
    isLoading: queryResult.isFetching,
    providerFlyoutData: queryResult.data?.provider,
  };
}

export default useBenchmarkingProviderFlyoutData;
